// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import { dispatch } from '../index';
import * as askJivanAPI from 'api/askJivan';
import * as alertSnackbar from 'utils/snackbar-wrapper';

const PENDING_QUESTION_ID = 'pending-question';

const initialState = {
    // whether platform admin has turned Ask Jivan on for this org
    enabled: false,
    open: false,
    // 'chat' | 'history'
    view: 'chat',
    // null until the first answer of a new conversation comes back
    conversationId: null,
    messages: [],
    // a question is with the model
    pending: false,
    loadingConversation: false,
    recentConversations: []
};

const slice = createSlice({
    name: 'askJivan',
    initialState,
    reducers: {
        setEnabled(state, action) {
            state.enabled = action.payload;
        },
        setOpen(state, action) {
            state.open = action.payload;
        },
        setView(state, action) {
            state.view = action.payload;
        },
        startNewConversation(state) {
            state.conversationId = null;
            state.messages = [];
            state.view = 'chat';
        },
        questionSent(state, action) {
            state.pending = true;
            state.messages.push({ id: PENDING_QUESTION_ID, role: 'USER', content: action.payload, citations: [] });
        },
        questionFailed(state) {
            state.pending = false;
            state.messages = state.messages.filter((message) => message.id !== PENDING_QUESTION_ID);
        },
        answerReceived(state, action) {
            const { conversationId, userMessageId, answer } = action.payload;
            state.pending = false;
            const question = state.messages.find((message) => message.id === PENDING_QUESTION_ID);
            // the thread was swapped or reset while the question was out: the answer
            // belongs to a conversation no longer showing, it stays in the history
            if (!question) {
                return;
            }
            question.id = userMessageId;
            state.conversationId = conversationId;
            state.messages.push(answer);
        },
        setLoadingConversation(state, action) {
            state.loadingConversation = action.payload;
        },
        conversationLoaded(state, action) {
            state.loadingConversation = false;
            state.conversationId = action.payload.conversation.id;
            state.messages = action.payload.messages;
            state.view = 'chat';
        },
        setRecentConversations(state, action) {
            state.recentConversations = action.payload;
        },
        setRating(state, action) {
            const message = state.messages.find(({ id }) => id === action.payload.messageId);
            if (message) {
                message.rating = action.payload.rating;
            }
        },
        resetState() {
            return initialState;
        }
    }
});

// Reducer
export default slice.reducer;

export const { setOpen, setView, startNewConversation } = slice.actions;

// ----------------------------------------------------------------------

// The axios interceptor rejects with the server's error body ({ statusCode, message })
// or the string 'Wrong Services' when there was no response.
function reasonFrom(e) {
    return typeof e === 'string' ? e : e?.message ?? 'unknown error';
}

export function resetAskJivanState() {
    dispatch(slice.actions.resetState());
}

export function getAskJivanEnabled() {
    return async () => {
        try {
            const response = await askJivanAPI.getAskJivanEnabled();
            dispatch(slice.actions.setEnabled(!!response.data.enabled));
        } catch (e) {
            // the widget is an extra: stay hidden without interrupting the page
            dispatch(slice.actions.setEnabled(false));
        }
    };
}

// Resolves true when the answer arrived, so the input only clears on success.
export function askQuestion(conversationId, question) {
    return async () => {
        dispatch(slice.actions.questionSent(question));
        try {
            const response = await askJivanAPI.askQuestion(conversationId, question);
            dispatch(slice.actions.answerReceived(response.data));
            return true;
        } catch (e) {
            dispatch(slice.actions.questionFailed());
            dispatch(alertSnackbar.error(`Jivan could not answer: ${reasonFrom(e)}`));
            return false;
        }
    };
}

export function getRecentConversations() {
    return async () => {
        try {
            const response = await askJivanAPI.getRecentConversations();
            dispatch(slice.actions.setRecentConversations(response.data.conversations));
        } catch (e) {
            dispatch(alertSnackbar.error(`Failed to fetch Ask Jivan conversations: ${reasonFrom(e)}`));
        }
    };
}

export function openConversation(conversationId) {
    return async () => {
        dispatch(slice.actions.setLoadingConversation(true));
        try {
            const response = await askJivanAPI.getConversation(conversationId);
            dispatch(slice.actions.conversationLoaded(response.data));
        } catch (e) {
            dispatch(slice.actions.setLoadingConversation(false));
            dispatch(alertSnackbar.error(`Failed to open the conversation: ${reasonFrom(e)}`));
        }
    };
}

export function rateAnswer(messageId, rating, comment) {
    return async () => {
        try {
            await askJivanAPI.rateAnswer(messageId, rating, comment);
            dispatch(slice.actions.setRating({ messageId, rating }));
        } catch (e) {
            dispatch(alertSnackbar.error(`Failed to save your feedback: ${reasonFrom(e)}`));
        }
    };
}
